import * as RadixTabs from '@radix-ui/react-tabs';
import { styled } from 'styled-components';

const RootSC = styled(RadixTabs.Root)`
  width: 100%;
`;

const ListSC = styled(RadixTabs.List)`
  background-color: ${(props) => props.theme.colors.slateGray[10]};
  border-radius: 1.625rem;
  display: flex;
  min-height: 2.625rem;
  justify-content: space-around;
  font-weight: 500;
  position: relative;
`;

const SliderSC = styled.div<{ $length: number }>`
  position: absolute;
  padding: 0.188rem;
  width: ${(props) => 100 / props.$length}%;
  height: 100%;
  left: 0;
  top: 0;
  z-index: 0;
  transition: 0.2s ease-out;
`;

const SliderBlockSC = styled.div`
  width: 100%;
  height: 100%;
  margin: auto;
  border-radius: 1.625rem;
  background-color: ${(props) => props.theme.colors.emeraldGreen[100]};
`;

const TriggerSC = styled(RadixTabs.Trigger)<{ $tabIndex: number }>`
  all: unset;
  font-size: ${(props) => props.theme.fontSizes.s};
  line-height: ${(props) => props.theme.lineHeights.xxs};
  color: ${(props) => props.theme.colors.oxfordBlue[100]};

  flex: 50%;
  margin-left: 0.3125rem;
  margin-right: 0.3125rem;
  margin-top: 0.1875rem;
  margin-bottom: 0.1875rem;
  padding: 0.25rem;
  text-align: center;
  letter-spacing: -0.08px;
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: color 0.2s ease-in;

  &[data-state='active'] {
    color: ${(props) => props.theme.colors.white};

    & ~ ${SliderSC} {
      transform: ${(props) => `translateX(${props.$tabIndex * 100}%)`};
    }
  }
`;

const ContentSC = styled(RadixTabs.Content)`
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  &[data-state='active'] {
    animation: fadeIn ease-in-out 0.2s;
  }
`;

export type Tab = {
  content: React.ReactNode;
  'data-test-id'?: string;
  title: string;
};

type Props = {
  'aria-label'?: string;
  className?: string;
  'data-test-id'?: string;
  defaultTab?: keyof Record<string, Tab>;
  rootProps?: RadixTabs.TabsProps;
  tabs: Record<string, Tab>;
};

export const Tabs = ({
  'aria-label': ariaLabel,
  className,
  'data-test-id': dataTestId,
  defaultTab,
  rootProps,
  tabs,
}: Props) => {
  const entries = Object.entries(tabs);
  const { length } = entries;

  if (!entries.length) {
    return null;
  }

  const [[firstTabKey]] = entries;

  const defaultValue = defaultTab ? defaultTab : firstTabKey;

  return (
    <RootSC {...rootProps} className={className} data-test-id={dataTestId} defaultValue={defaultValue}>
      <ListSC aria-label={ariaLabel}>
        {entries.map(([key, value], index) => (
          <TriggerSC key={key} $tabIndex={index} data-test-id={value['data-test-id']} value={key}>
            {value.title}
          </TriggerSC>
        ))}
        <SliderSC $length={length}>
          <SliderBlockSC />
        </SliderSC>
      </ListSC>
      {entries.map(([key, value]) => (
        <ContentSC key={key} value={key}>
          {value.content}
        </ContentSC>
      ))}
    </RootSC>
  );
};
